import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Badge, Box, CircularProgress, IconButton, List, ListItemButton, ListItemText, Popover, Tooltip, Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import knowledgeService, { notePath, openNotesPage } from '../../services/knowledgeService';

/**
 * "Notes for this screen" (FR-81): Biztimize Notes mapped to the navigation key
 * of the page the user is on. Loaded only when opened, so it costs nothing on
 * pages nobody asks about.
 */
const ScreenNotesButton = () => {
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const [state, setState] = useState({ loading: false, results: [], path: '' });

  const open = async (event) => {
    setAnchor(event.currentTarget);
    if (state.path === location.pathname) return;
    setState({ loading: true, results: [], path: location.pathname });
    try {
      const { data } = await knowledgeService.notesForScreen(location.pathname);
      setState({ loading: false, results: data.results || [], path: location.pathname });
    } catch {
      setState({ loading: false, results: [], path: location.pathname });
    }
  };

  return (
    <>
      <Tooltip title="Biztimize Notes for this screen">
        <IconButton color="inherit" onClick={open} aria-label="Biztimize Notes for this screen">
          <Badge color="secondary" variant="dot" invisible={!(state.path === location.pathname && state.results.length)}>
            <MenuBookIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 360, maxWidth: '90vw', p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Biztimize Notes for this screen</Typography>
          {state.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
          ) : state.results.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No Notes are linked to this screen.</Typography>
          ) : (
            <List dense disablePadding>
              {state.results.map((n) => (
                <ListItemButton key={n.number} onClick={() => openNotesPage(notePath(n.number))}>
                  <ListItemText primary={`${n.number} — ${n.title}`} secondary={`${n.category_label} · ${n.priority_label}`} />
                </ListItemButton>
              ))}
            </List>
          )}
          <ListItemButton onClick={() => openNotesPage('/notes/')} sx={{ mt: 0.5 }}>
            <ListItemText primary="Search all Biztimize Notes" primaryTypographyProps={{ color: 'primary', variant: 'body2' }} />
          </ListItemButton>
        </Box>
      </Popover>
    </>
  );
};

export default ScreenNotesButton;
